/**
 * API Performance Monitor
 * Tracks all API calls to identify duplicates and performance issues
 */

class ApiPerformanceMonitor {
  constructor() {
    this.calls = [];
    this.duplicates = [];
    // Always enable - it's lightweight and useful
    this.enabled = true;
    this.setupInterception();
  }

  setupInterception() {
    // Intercept fetch calls
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = async function(...args) {
      const url = typeof args[0] === 'string' ? args[0] : args[0].url;
      const startTime = performance.now();
      const callId = `${url}_${Date.now()}_${Math.random()}`;
      
      // Log the call
      const callInfo = {
        id: callId,
        url,
        method: args[1]?.method || 'GET',
        timestamp: new Date().toISOString(),
        startTime,
        status: 'pending'
      };
      
      self.calls.push(callInfo);
      self.checkForDuplicates(url, callId);
      
      try {
        const response = await originalFetch.apply(this, args);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        callInfo.status = response.status;
        callInfo.duration = duration;
        callInfo.endTime = endTime;
        callInfo.success = response.ok;
        
        // Extract Firestore operation counts from response headers
        const firestoreReads = parseInt(response.headers.get('X-Firestore-Reads') || '0');
        const firestoreWrites = parseInt(response.headers.get('X-Firestore-Writes') || '0');
        const firestoreDeletes = parseInt(response.headers.get('X-Firestore-Deletes') || '0');
        const firestoreTotal = parseInt(response.headers.get('X-Firestore-Total') || '0');
        const firestoreEndpoint = response.headers.get('X-Firestore-Endpoint') || '';
        
        if (firestoreTotal > 0) {
          callInfo.firestoreStats = {
            reads: firestoreReads,
            writes: firestoreWrites,
            deletes: firestoreDeletes,
            total: firestoreTotal,
            endpoint: firestoreEndpoint
          };
          
          // Track in Firestore monitor if available
          if (window.firestoreMonitor && firestoreTotal > 0) {
            window.firestoreMonitor.trackFromApiResponse(
              firestoreReads,
              firestoreWrites,
              firestoreDeletes,
              firestoreEndpoint
            );
          }
        }
        
        // Log slow calls (> 1 second)
        if (duration > 1000) {
          console.warn(`🐌 Slow API call: ${url} took ${duration.toFixed(2)}ms`);
        }
        
        return response;
      } catch (error) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        callInfo.status = 'error';
        callInfo.duration = duration;
        callInfo.endTime = endTime;
        callInfo.error = error.message;
        callInfo.success = false;
        
        console.error(`❌ API call failed: ${url}`, error);
        throw error;
      }
    };
  }

  checkForDuplicates(url, currentCallId) {
    const recentCalls = this.calls.filter(call => 
      call.url === url && 
      call.id !== currentCallId &&
      call.status === 'pending' &&
      (performance.now() - call.startTime) < 1000 // Within last second
    );
    
    if (recentCalls.length > 0) {
      const duplicate = {
        url,
        count: recentCalls.length + 1,
        calls: [...recentCalls.map(c => c.id), currentCallId],
        timestamp: new Date().toISOString()
      };
      
      this.duplicates.push(duplicate);
      console.warn(`⚠️ Duplicate API call detected: ${url} (${duplicate.count} simultaneous calls)`);
    }
  }

  getReport() {
    const report = {
      totalCalls: this.calls.length,
      successfulCalls: this.calls.filter(c => c.success).length,
      failedCalls: this.calls.filter(c => !c.success).length,
      duplicates: this.duplicates.length,
      averageDuration: this.calls
        .filter(c => c.duration)
        .reduce((sum, c) => sum + c.duration, 0) / this.calls.filter(c => c.duration).length || 0,
      slowCalls: this.calls.filter(c => c.duration > 1000),
      duplicateDetails: this.duplicates,
      callsByUrl: this.groupByUrl(),
      timeline: this.calls.map(c => ({
        url: c.url,
        startTime: c.startTime,
        duration: c.duration,
        status: c.status
      }))
    };
    
    return report;
  }

  groupByUrl() {
    const grouped = {};
    this.calls.forEach(call => {
      if (!grouped[call.url]) {
        grouped[call.url] = {
          count: 0,
          totalDuration: 0,
          averageDuration: 0,
          successCount: 0,
          failCount: 0
        };
      }
      
      grouped[call.url].count++;
      if (call.duration) {
        grouped[call.url].totalDuration += call.duration;
      }
      if (call.success) {
        grouped[call.url].successCount++;
      } else {
        grouped[call.url].failCount++;
      }
    });
    
    // Calculate averages
    Object.keys(grouped).forEach(url => {
      grouped[url].averageDuration = grouped[url].totalDuration / grouped[url].count;
    });
    
    return grouped;
  }

  printReport() {
    const report = this.getReport();
    
    console.group('📊 API Performance Report');
    console.log('Total Calls:', report.totalCalls);
    console.log('Successful:', report.successfulCalls);
    console.log('Failed:', report.failedCalls);
    console.log('Duplicates:', report.duplicates);
    console.log('Average Duration:', `${report.averageDuration.toFixed(2)}ms`);
    console.log('Slow Calls (>1s):', report.slowCalls.length);
    
    if (report.duplicateDetails.length > 0) {
      console.group('⚠️ Duplicate Calls');
      report.duplicateDetails.forEach(dup => {
        console.warn(`${dup.url}: ${dup.count} simultaneous calls`);
      });
      console.groupEnd();
    }
    
    if (report.slowCalls.length > 0) {
      console.group('🐌 Slow Calls (>1s)');
      report.slowCalls.forEach(call => {
        console.warn(`${call.url}: ${call.duration.toFixed(2)}ms`);
      });
      console.groupEnd();
    }
    
    console.group('📈 Calls by URL');
    Object.entries(report.callsByUrl).forEach(([url, stats]) => {
      console.log(`${url}:`, {
        count: stats.count,
        avgDuration: `${stats.averageDuration.toFixed(2)}ms`,
        success: stats.successCount,
        failed: stats.failCount
      });
    });
    console.groupEnd();
    
    console.groupEnd();
    
    return report;
  }

  clear() {
    this.calls = [];
    this.duplicates = [];
  }
}

// Create singleton instance
const apiMonitor = new ApiPerformanceMonitor();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  window.apiMonitor = apiMonitor;
  
  // Auto-print report after page load
  window.addEventListener('load', () => {
    setTimeout(() => {
      console.log('📊 Page load complete. Generating API performance report...');
      apiMonitor.printReport();
    }, 3000); // Wait 3 seconds after page load to catch all calls
  });
  
  // Log immediately when monitor is initialized
  console.log('🔍 API Performance Monitor enabled. Monitoring all API calls...');
}

export default apiMonitor;

